import React, { forwardRef, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FormTextField } from '../../common/Form/FormTextField';
import { LIKERT_STEPS } from '../../common/LikertFaceScale';
import { FEEDBACK_DETAIL_MAX_LENGTH } from '../../common/constants';
import { formatDate, formatTime } from '../../common/helpers';
import { RatingFace } from './columns';

/** One labelled fact about the submission, or an em dash where there is none. */
const Fact = ({ id, label, children }) => (
  <Box className="feedbackFact" id={id}>
    <Typography className="mss__label feedbackFactLabel" component="span">
      {label}
    </Typography>
    <Typography className="feedbackFactValue" component="span">
      {children || '—'}
    </Typography>
  </Box>
);

/** One of the four things a learner can write, shown only when they wrote it. */
const Answer = ({ id, label, text }) =>
  text ? (
    <Box className="feedbackAnswer" id={id}>
      <Typography className="mss__label feedbackAnswerLabel">{label}</Typography>
      <Typography className="feedbackAnswerText">{text}</Typography>
    </Box>
  ) : null;

/**
 * One submission, read in full: who sent it, what they thought, what they wrote
 * and what the browser was — plus the note a reviewer leaves for the team.
 *
 * Takes a ref on its outermost box — the same element whether or not anything
 * is selected — so a phone, where this sits under the list rather than beside
 * it, can scroll to the submission that was just tapped.
 *
 * Props:
 *   id         (string)   - ID prefix for generated element IDs.
 *   feedback   (object)   - The selected submission, or null for none.
 *   isSaving   (bool)     - Whether a note is being written just now.
 *   onSaveNote (function) - Called with the note to store against this one.
 */
const FeedbackDetails = forwardRef(({ id, feedback, isSaving, onSaveNote }, ref) => {
  const [note, setNote] = useState('');

  // The note belongs to the submission on screen, so it is reread on each one.
  useEffect(() => {
    setNote(feedback?.note ?? '');
  }, [feedback]);

  if (!feedback) {
    return (
      <Box className="mss__box feedbackDetails empty" id={`${id}__box_detailsEmpty`} ref={ref}>
        <Typography className="feedbackEmptyText">
          Pick a row to read the whole of it here.
        </Typography>
      </Box>
    );
  }

  const step = LIKERT_STEPS[feedback.rating];
  const isNoteDirty = note !== (feedback.note ?? '');

  return (
    <Box className="mss__box feedbackDetails" id={`${id}__box_details`} ref={ref}>
      <Typography variant="h2" component="h2" className="feedbackDetailsHeading">
        Submission
      </Typography>

      <Box className="feedbackFacts">
        <Fact id={`${id}__text_user`} label="User">
          {feedback.userId}
        </Fact>
        <Fact id={`${id}__text_submitted`} label="Submitted">
          {feedback.createdAt &&
            `${formatDate(feedback.createdAt)}, ${formatTime(feedback.createdAt)}`}
        </Fact>
        <Fact id={`${id}__text_course`} label="Course">
          {feedback.courseId}
        </Fact>
        <Fact id={`${id}__text_rating`} label="Rating">
          {step && (
            <Box className="feedbackRatingValue" component="span">
              <RatingFace rating={feedback.rating} /> {step.label}
            </Box>
          )}
        </Fact>
      </Box>

      <Answer
        id={`${id}__text_changeDetail`}
        label="What they would change"
        text={feedback.changeDetail}
      />
      <Answer id={`${id}__text_pageUrl`} label="Where the bug was" text={feedback.pageUrl} />
      <Answer id={`${id}__text_bugDetail`} label="The bug" text={feedback.bugDetail} />
      <Answer id={`${id}__text_ideaDetail`} label="Their idea" text={feedback.ideaDetail} />
      <Answer id={`${id}__text_otherDetail`} label="Anything else" text={feedback.otherDetail} />

      {/*
        Collapsed by default: it is here for the one bug report that has to be
        reproduced somewhere, not for reading alongside what the learner said.
      */}
      {feedback.metadata && (
        <Box className="feedbackMetadata" component="details" id={`${id}__details_metadata`}>
          <Typography component="summary" className="mss__label feedbackMetadataSummary">
            Browser
          </Typography>
          <Box component="pre" className="feedbackMetadataBody">
            {JSON.stringify(feedback.metadata, null, 2)}
          </Box>
        </Box>
      )}

      <Box className="feedbackNote">
        <FormTextField
          id={`${id}__input_note`}
          label="Reviewer note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          htmlInputProps={{ maxLength: FEEDBACK_DETAIL_MAX_LENGTH }}
          placeholder="What was done about this, for whoever reads it next"
          multiline
          minRows={3}
          fullWidth
        />
        <Box className="mss__formActions feedbackNoteActions">
          <Button
            variant="contained"
            className="mss__button primary"
            id={`${id}__button_saveNote`}
            disabled={!isNoteDirty || isSaving}
            onClick={() => onSaveNote(note)}
          >
            Save Note
          </Button>
        </Box>
      </Box>
    </Box>
  );
});

FeedbackDetails.displayName = 'FeedbackDetails';

export default FeedbackDetails;
